import React, { useState } from 'react';
import { useToast, ToastContainer } from '../common/Toast';

const ApiDiagnostic: React.FC = () => {
  const { toasts, showSuccess, showError } = useToast();
  const [results, setResults] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const testEndpoint = async (url: string, method: 'GET' | 'POST' = 'GET', data?: any) => {
    try {
      const options: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      };

      if (data && method === 'POST') {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);
      const responseData = await response.json();
      
      return {
        status: response.status,
        statusText: response.statusText,
        data: responseData,
        headers: Object.fromEntries(response.headers.entries())
      };
    } catch (error: any) {
      return {
        error: error.message,
        status: 'ERROR'
      };
    }
  };

  const runDiagnostics = async () => {
    setLoading(true);
    const diagnosticResults: any = {};

    try {
      // Test 1: Configuration de base
  
      diagnosticResults.config = {
        baseUrl: (import.meta as any).env?.VITE_API_BASE_URL || 'https://api.local.ci',
        apiVersion: 'v1'
      };

      // Test 2: Endpoint des sites
 
      const sitesUrl = 'https://api.local.ci/api/v1/sites/';
      diagnosticResults.sites = await testEndpoint(sitesUrl);

      // Test 3: Endpoint des rôles

      const rolesUrl = 'https://api.local.ci/api/v1/auth/roles/';
      diagnosticResults.roles = await testEndpoint(rolesUrl);

      // Test 4: Endpoint des utilisateurs

      const usersUrl = 'http://localhost:8000/api/v1/users/';
      diagnosticResults.users = await testEndpoint(usersUrl);

      // Test 5: Endpoint d'assignation (tentative)

      const assignUrl = 'http://localhost:8000/api/v1/users/5/assign-site/';
      diagnosticResults.assignSite = await testEndpoint(assignUrl, 'POST', {
        user_id: '5',
        site_id: 1,
        role_id: 'admin',
        session_type: 'PERMANENTE'
      });

      // Test 6: Endpoint d'assignation alternatif

      const assignAltUrl = 'http://localhost:8000/api/v1/users/5/sites/';
      diagnosticResults.assignSiteAlt = await testEndpoint(assignAltUrl, 'POST', {
        site_id: 1,
        role_id: 'admin'
      });

      // Test 7: Endpoint de gestion des sites utilisateur
      
      const userSitesUrl = 'http://localhost:8000/api/v1/users/5/sites/';
      diagnosticResults.userSites = await testEndpoint(userSitesUrl);

      // Test 8: Endpoint de création de session
      console.log('🔍 Test 8: Endpoint de création de session');
      const sessionUrl = 'http://localhost:8000/api/v1/users/5/sessions/';
      diagnosticResults.userSessions = await testEndpoint(sessionUrl, 'POST', {
        site_id: 1,
        role_id: 'admin',
        type: 'PERMANENTE'
      });

      setResults(diagnosticResults);
      showSuccess('Diagnostic terminé', 'Tous les tests ont été exécutés. Vérifiez la console et les résultats ci-dessous.');

    } catch (error: any) {
      console.error('❌ Erreur lors du diagnostic:', error);
      showError('Erreur', `Erreur lors du diagnostic: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: number | string) => {
    if (status === 'ERROR') return 'text-red-600';
    if (typeof status === 'number') {
      if (status >= 200 && status < 300) return 'text-green-600';
      if (status >= 400 && status < 500) return 'text-orange-600';
      if (status >= 500) return 'text-red-600';
    }
    return 'text-gray-600';
  };

  const getStatusIcon = (status: number | string) => {
    if (status === 'ERROR') return '❌';
    if (typeof status === 'number') {
      if (status >= 200 && status < 300) return '✅';
      if (status >= 400 && status < 500) return '⚠️';
      if (status >= 500) return '❌';
    }
    return '❓';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">🔍 Diagnostic de l'API</h1>
          
          <div className="mb-6">
            <button
              onClick={runDiagnostics}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-lg font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Diagnostic en cours...
                </>
              ) : (
                <>
                  🚀 Lancer le Diagnostic Complet
                </>
              )}
            </button>
          </div>

          {/* Configuration */}
          {results.config && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-medium text-blue-900 mb-2">⚙️ Configuration API</h3>
              <div className="text-sm text-blue-700">
                <p><strong>Base URL:</strong> {results.config.baseUrl}</p>
                <p><strong>API Version:</strong> {results.config.apiVersion}</p>
              </div>
            </div>
          )}

          {/* Résultats des tests */}
          {Object.keys(results).length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">📊 Résultats des Tests</h3>
              
              {Object.entries(results).map(([key, result]: [string, any]) => {
                if (key === 'config') return null;
                
                return (
                  <div key={key} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </h4>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className={getStatusColor(result.status)}>
                          {getStatusIcon(result.status)} Status: {result.status}
                        </span>
                        {result.statusText && (
                          <span className="text-gray-500">({result.statusText})</span>
                        )}
                      </div>
                      
                      {result.error && (
                        <div className="text-red-600">
                          <strong>Erreur:</strong> {result.error}
                        </div>
                      )}
                      
                      {result.data && (
                        <div className="bg-gray-50 p-2 rounded text-xs">
                          <strong>Données:</strong> {JSON.stringify(result.data, null, 2)}
                        </div>
                      )}
                      
                      {result.headers && Object.keys(result.headers).length > 0 && (
                        <div className="bg-gray-50 p-2 rounded text-xs">
                          <strong>Headers:</strong> {JSON.stringify(result.headers, null, 2)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Instructions */}
          <div className="mt-8 p-4 bg-yellow-50 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-900 mb-2">📋 Instructions</h3>
            <div className="text-sm text-yellow-800 space-y-2">
              <p>1. Cliquez sur "Lancer le Diagnostic Complet"</p>
              <p>2. Attendez que tous les tests soient terminés</p>
              <p>3. Vérifiez les résultats ci-dessus</p>
              <p>4. Consultez la console du navigateur pour plus de détails</p>
              <p>5. Identifiez les endpoints qui fonctionnent et ceux qui échouent</p>
            </div>
          </div>

          {/* Recommandations */}
          {Object.keys(results).length > 0 && (
            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <h3 className="text-lg font-medium text-green-900 mb-2">💡 Recommandations</h3>
              <div className="text-sm text-green-800 space-y-2">
                {results.assignSite?.status >= 400 && (
                  <p>• L'endpoint d'assignation n'existe pas encore. Il faut l'implémenter côté backend.</p>
                )}
                {results.assignSiteAlt?.status >= 200 && results.assignSiteAlt?.status < 300 && (
                  <p>• L'endpoint alternatif d'assignation fonctionne. Utilisez-le pour l'assignation.</p>
                )}
                {results.userSites?.status >= 200 && results.userSites?.status < 300 && (
                  <p>• L'endpoint de gestion des sites utilisateur fonctionne.</p>
                )}
                <p>• Vérifiez la documentation de l'API pour connaître les endpoints disponibles.</p>
                <p>• Adaptez le code frontend selon les endpoints réellement disponibles.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Container des toasts */}
      <ToastContainer toasts={toasts} position="top-right" />
    </div>
  );
};

export default ApiDiagnostic;
